import { ExternalLink, Mail } from 'lucide-react';
import { Hint } from '@/components/Hint';
import { cn } from '@/lib/utils';
import { sundaeMessageUrl } from '@/api/mail';
import { useAuth } from '@/auth/useAuth';
import { useMailPreviewStore } from '@/features/mail-preview/mailPreviewStore';

/**
 * Small chip that opens a journey's source email (confirmation, invoice email,
 * manifest, announcement) in the standalone MailPreviewDialog. The page
 * mounting this must also mount <MailPreviewDialog />.
 *
 * There is no account here: the orders app stores one snapshot per globally
 * unique mail message id and knows nothing about mailboxes (plan §2 rule 1).
 * Without `orders:read:mail` the chip degrades to the "Open in sundae" deep link
 * alone — the reference is still worth naming even when the body isn't ours
 * to show.
 */
export function SourceEmailLink({
  messageId,
  journeyId,
  label,
  hint,
  className,
}: {
  messageId: string;
  /** Scopes the preview's thread expansion to this journey's vendor domains. */
  journeyId?: string | null;
  label: string;
  hint?: string;
  className?: string;
}) {
  const openPreview = useMailPreviewStore((state) => state.openPreview);
  const { can } = useAuth();

  const chipClass = cn(
    'inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-2 py-0.5 text-xs text-primary hover:bg-primary/5 hover:underline',
    className
  );

  if (!can('orders:read:mail')) {
    return (
      <Hint content={`Open the ${label} email in sundae`}>
        <a href={sundaeMessageUrl(messageId)} target="_blank" rel="noreferrer" className={chipClass}>
          <ExternalLink className="h-3 w-3" />
          {label}
        </a>
      </Hint>
    );
  }

  return (
    <Hint content={hint || `Open the ${label} email`}>
      <button
        type="button"
        className={chipClass}
        data-testid={`source-email-${messageId}`}
        onClick={() => openPreview(messageId, { journeyId, label })}
      >
        <Mail className="h-3 w-3" />
        {label}
      </button>
    </Hint>
  );
}
